import React, { useRef, useMemo, useState } from 'react';
import { Button, Popover } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { IconChevronDown } from '@tabler/icons-react';
import dayjs from 'dayjs';
import useOutsideClick from '../../hooks/useOutsideClick';

interface DateRangePickerWithPresetsProps {
    value: [Date | null, Date | null];
    onChange: (range: [Date | null, Date | null]) => void;
    buttonLabel?: string;
    disabled?: boolean;
    presets?: { value: [string, string]; label: string }[];
    buttonSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
}

const defaultPresets = () => {
    const today = dayjs();
    const lastPassedSunday = today.subtract(
        today.day() === 0 ? 7 : today.day(),
        'day',
    );
    return [
        {
            value: [
                today.subtract(30, 'days').format('YYYY-MM-DD'),
                today.format('YYYY-MM-DD'),
            ] as [string, string],
            label: 'Last 30 Days',
        },
        {
            value: [
                today.startOf('week').format('YYYY-MM-DD'),
                today.endOf('week').format('YYYY-MM-DD'),
            ] as [string, string],
            label: 'This Week',
        },
        {
            value: [
                today.subtract(1, 'week').startOf('week').format('YYYY-MM-DD'),
                today.subtract(1, 'week').endOf('week').format('YYYY-MM-DD'),
            ] as [string, string],
            label: 'Last Week',
        },
        {
            value: [
                lastPassedSunday.subtract(4, 'weeks').format('YYYY-MM-DD'),
                lastPassedSunday.format('YYYY-MM-DD'),
            ] as [string, string],
            label: 'Last 4 Weeks',
        },
        {
            value: [
                today.startOf('month').format('YYYY-MM-DD'),
                today.endOf('month').format('YYYY-MM-DD'),
            ] as [string, string],
            label: 'This Month',
        },
        {
            value: [
                today
                    .subtract(1, 'month')
                    .startOf('month')
                    .format('YYYY-MM-DD'),
                today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
            ] as [string, string],
            label: 'Last Month',
        },
        {
            value: [
                today.startOf('year').format('YYYY-MM-DD'),
                today.endOf('year').format('YYYY-MM-DD'),
            ] as [string, string],
            label: 'This Year',
        },
        {
            value: [
                today.subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
                today.subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
            ] as [string, string],
            label: 'Last Year',
        },
    ];
};

function dateRangeToLabel(range: [Date | null, Date | null]) {
    if (!range[0] && !range[1]) return 'Select Date Range';
    if (range[0] && !range[1]) return dayjs(range[0]).format('D MMM YYYY');
    if (!range[0] && range[1]) return dayjs(range[1]).format('D MMM YYYY');
    return `${dayjs(range[0]).format('D MMM YYYY')} - ${dayjs(range[1]).format(
        'D MMM YYYY',
    )}`;
}

function DateRangePickerWithPresets({
    value,
    onChange,
    buttonLabel,
    disabled,
    presets,
    buttonSize = 'md',
    className,
}: DateRangePickerWithPresetsProps) {
    const [opened, setOpened] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);
    const datePresets = useMemo(() => presets || defaultPresets(), [presets]);

    useOutsideClick(popoverRef, opened, () => setOpened(false), [
        '[data-dates-input]',
        '.mantine-DatePicker-root',
        '.mantine-Popover-dropdown',
        '[role="dialog"]',
    ]);

    return (
        <div ref={popoverRef} className={className}>
            <Popover
                opened={opened}
                onClose={() => setOpened(false)}
                closeOnClickOutside
                shadow="md"
            >
                <Popover.Target>
                    <Button
                        fullWidth={false}
                        size={buttonSize}
                        variant="outline"
                        rightSection={<IconChevronDown size={20} />}
                        onClick={() => setOpened(true)}
                        disabled={disabled}
                    >
                        {buttonLabel || dateRangeToLabel(value)}
                    </Button>
                </Popover.Target>
                <Popover.Dropdown>
                    <DatePicker
                        type="range"
                        numberOfColumns={2}
                        value={
                            value.map(date =>
                                date ? dayjs(date).format('YYYY-MM-DD') : null,
                            ) as [string | null, string | null]
                        }
                        onChange={val =>
                            onChange([
                                val?.[0] ? dayjs(val[0]).toDate() : null,
                                val?.[1] ? dayjs(val[1]).toDate() : null,
                            ])
                        }
                        presets={datePresets}
                    />
                </Popover.Dropdown>
            </Popover>
        </div>
    );
}

export default DateRangePickerWithPresets;
